<template>
  <div class="practice-general">
    <v-container>
      <bh-text-input
        label="Registered"
        :value="creationDate"
        inputEditingBackgroundClass="bhBlue4"
        readonlyAsSpan
        readonly
        prependIcon="mdi-calendar-blank-outline"
      />
      <v-row>
        <v-col cols="12" md="6">
          <p class="section-title">Company data</p>
          <component
            class="practice-general-field"
            :class="
              `practice-general-field-${field.component} ${
                !isAnyAdminRole ? 'practice-general-field--readonly' : ''
              }`
            "
            :is="field.component"
            v-for="field in practiceFields"
            :label="field.label"
            :key="field.name"
            :value="practice[field.name]"
            :id="field.name"
            :items="field.items"
            inputEditingBackgroundClass="bhBlue4"
            :showConfirmEdit="isPracticeActive && isAnyAdminRole"
            @input="inputUpdate(field.name, $event)"
            :validate="field.validate"
            :validationType="field.validationType"
            :readonly="!isPracticeActive || !isAnyAdminRole"
          />
          <template
            v-if="
              isPracticeActive &&
                (isAnyAdminRole || roleAllowedToManageJournalFlag)
            "
          >
            <p class="section-title">Feature Settings</p>
            <bh-select
              class="mb-3"
              :disabled="!isAnyAdminRole || !isPracticeActive"
              :label="tenantTexts.companyDetails.swapFlag"
              v-model="practice.swapAvailable"
              :items="computedFeatureFlagsSwap"
              item-text="text"
              item-value="value"
              @input="changeFeatureFlag('swapAvailable', $event)"
            />
            <bh-select
              v-if="featuresSettings.VUE_FEATURE_SMART_SURVEY"
              class="mb-3"
              :disabled="!isAnyAdminRole || !isPracticeActive"
              :label="tenantTexts.companyDetails.smartSurveyFlag"
              v-model="practice.smartSurveyAvailable"
              :items="computedFeatureFlagsSmartSurvey"
              item-text="text"
              item-value="value"
              @input="changeFeatureFlag('smartSurveyAvailable', $event)"
            />
            <bh-select
              class="mb-3"
              :disabled="!roleAllowedToManageJournalFlag || !isPracticeActive"
              :label="tenantTexts.companyDetails.journalFlag"
              v-model="practice.journalAvailable"
              :items="computedFeatureFlagsJournal"
              item-text="text"
              item-value="value"
              @input="changeFeatureFlag('journalAvailable', $event)"
            />
            <bh-select
              v-if="featuresSettings.VUE_FEATURE_SCREENER"
              class="mb-3"
              :disabled="!isAnyAdminRole || !isPracticeActive"
              :label="tenantTexts.companyDetails.surveyFlag"
              v-model="practice.surveyAvailable"
              :items="computedFeatureFlagsSurvey"
              item-text="text"
              item-value="value"
              @input="changeFeatureFlag('surveyAvailable', $event)"
            />
          </template>
        </v-col>
        <v-col cols="12" md="6">
          <p class="section-title">Primary contact</p>
          <component
            class="practice-general-field"
            :class="`practice-general-field-${field.component}`"
            :is="field.component"
            v-for="field in contactFields"
            :label="field.label"
            :key="field.name"
            :value="practice[field.name]"
            :id="field.name"
            :items="field.items"
            inputEditingBackgroundClass="bhBlue4"
            :showConfirmEdit="isPracticeActive && isAnyAdminRole"
            @input="inputUpdate(field.name, $event)"
            :readonly="!isPracticeActive || !isAnyAdminRole"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col class="d-flex align-center justify-end">
          <v-btn
            v-if="!practice.isActive"
            :disabled="!isAnyAdminRole"
            text
            color="bhRedDark"
            class="rounded-pill mr-2"
            elevation="0"
            @click="promptDelete"
          >
            Delete company
          </v-btn>
          <v-btn
            color="accent"
            class="rounded-pill"
            elevation="0"
            v-if="activationAllowed && isAnyAdminRole"
            @click="promptActivateState"
          >
            {{ practiceStatusText }} company
          </v-btn>
        </v-col>
      </v-row>
      <v-dialog
        v-model="confirmationPopupActiveStatus"
        persistent
        max-width="290"
        @click:outside="onClickOutsideConfirmationActive"
        @keydown="onKeydownConfirmationActive"
      >
        <v-card>
          <v-card-title class="headline"
            >{{ practiceStatusText }} company?</v-card-title
          >
          <v-card-text>Are you sure?</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              class="rounded-pill"
              text
              @click="closeConfirmationPopupActiveStatus"
              >Cancel</v-btn
            >
            <v-btn
              class="rounded-pill elevation-0"
              color="accent"
              @click="
                closeConfirmationPopupActiveStatus()
                toggleActiveState()
              "
              >{{ practiceStatusText }}</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="deletePopupActiveStatus"
        persistent
        max-width="290"
        @click:outside="onClickOutsideDeleteActive"
        @keydown="onKeydownDeleteActive"
      >
        <v-card>
          <v-card-title class="headline">Delete company?</v-card-title>
          <v-card-text>Are you sure?</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              class="rounded-pill"
              text
              @click="closeDeletePopupActiveStatus"
              >Cancel</v-btn
            >
            <v-btn
              class="rounded-pill elevation-0"
              color="accent"
              @click="
                closeDeletePopupActiveStatus()
                deleteCompany()
              "
              >Delete</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import BhTextInput from '@/components/inputs/BhTextInput.vue'
import BhSelect from '@/components/inputs/BhSelect.vue'
import TenantTextsMixin from '@/mixins/tenantTexts'
import FeaturesSettingsMixin from '@/mixins/featuresSettings'

export default {
  name: 'PracticeGeneral',
  components: {
    BhTextInput,
    BhSelect,
  },
  mixins: [TenantTextsMixin, FeaturesSettingsMixin],
  data() {
    return {
      confirmationPopupActiveStatus: false,
      deletePopupActiveStatus: false,
      practiceFields: [
        {
          name: 'name',
          label: 'Name',
          component: 'bh-text-input',
        },
        {
          name: 'type',
          label: 'Type',
          component: 'bh-text-input',
        },
        {
          name: 'address',
          label: 'Address',
          component: 'bh-text-input',
        },
        {
          name: 'phoneNo',
          label: 'Phone number',
          component: 'bh-text-input',
          type: 'tel',
        },
        {
          name: 'email',
          label: 'Email address',
          component: 'bh-text-input',
          validate: true,
          validationType: 'email',
        },
      ],
      contactFields: [
        {
          name: 'pcFirstName',
          label: 'First name',
          component: 'bh-text-input',
        },
        {
          name: 'pcLastName',
          label: 'Last name',
          component: 'bh-text-input',
        },
        {
          name: 'pcPhoneNo',
          label: 'Mobile number',
          component: 'bh-text-input',
        },
        {
          name: 'pcEmail',
          label: 'Email address',
          component: 'bh-text-input',
        },
      ],
    }
  },
  computed: {
    ...mapState('practicesStates', ['practice']),
    ...mapGetters('userStates', ['getRolename']),
    ...mapState('dictionariesStates', [
      'dictionaryBhUsersRoleIds',
      'dictionaryPracticeUserRole',
      'dictionaryFeatureFlagState',
    ]),
    activationAllowed() {
      return this.dictionaryBhUsersRoleIds[this.getRolename]
    },
    routePracticeId() {
      return this.$route.params.id
    },
    creationDate() {
      return this.$moment(this.practice.creationDate).format('MMMM Do, YYYY')
    },
    practiceStatusText() {
      return this.practice.isActive ? 'Deactivate' : 'Activate'
    },
    isAnyAdminRole() {
      // SS Admin and Company Admin
      return (
        this.dictionaryBhUsersRoleIds[this.getRolename] ===
          this.dictionaryBhUsersRoleIds.Admin ||
        this.dictionaryPracticeUserRole[this.getRolename] ===
          this.dictionaryPracticeUserRole.Administrator
      )
    },
    roleAllowedToManageJournalFlag() {
      return (
        this.dictionaryBhUsersRoleIds[this.getRolename] ===
          this.dictionaryBhUsersRoleIds.Admin ||
        this.dictionaryPracticeUserRole[this.getRolename] ===
          this.dictionaryPracticeUserRole.Administrator
      )
    },
    computedFeatureFlagsJournal() {
      return [
        {
          text: 'Journal & Metrics',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Active
          ],
        },
        {
          text: 'Journal only',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Partial
          ],
        },
        {
          text: 'Journal off',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Inactive
          ],
        },
      ]
    },
    computedFeatureFlagsSmartSurvey() {
      return [
        {
          text: 'Full survey',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Active
          ],
        },
        {
          text: 'Initial questions only',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Partial
          ],
        },
        {
          text: 'Survey off',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Inactive
          ],
        },
      ]
    },
    computedFeatureFlagsSwap() {
      return [
        {
          text: 'Active',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Active
          ],
        },
        {
          text: 'Inactive',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Inactive
          ],
        },
      ]
    },
    computedFeatureFlagsSurvey() {
      return [
        {
          text: 'Active',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Active
          ],
        },
        {
          text: 'Inactive',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Inactive
          ],
        },
      ]
    },
    isPracticeActive() {
      return this.practice.isActive
    },
    isConsultant() {
      return (
        this.dictionaryPracticeUserRole[this.getRolename] ===
        this.dictionaryPracticeUserRole.Consultant
      )
    },
  },
  methods: {
    ...mapActions('practicesStates', [
      'changePracticeField',
      'changePracticeFeatureFlag',
      'deletePractice',
    ]),
    inputUpdate(key, value) {
      this.changePracticeField({ key, value, id: this.routePracticeId })
    },
    changeFeatureFlag(key, value) {
      this.changePracticeFeatureFlag({ key, value, id: this.routePracticeId })
    },
    promptActivateState() {
      this.confirmationPopupActiveStatus = true
    },
    promptDelete() {
      this.deletePopupActiveStatus = true
    },
    toggleActiveState() {
      this.changePracticeField({
        key: 'isActive',
        value: !this.practice.isActive,
        id: this.routePracticeId,
      }) // status is a string so far
    },
    deleteCompany() {
      this.deletePractice(this.practice.id).then(() => {
        this.$router.push({ name: 'Practices', path: '/practices/' })
      })
    },
    closeConfirmationPopupActiveStatus() {
      this.confirmationPopupActiveStatus = false
    },
    closeDeletePopupActiveStatus() {
      this.deletePopupActiveStatus = false
    },
    onClickOutsideConfirmationActive() {
      this.closeConfirmationPopupActiveStatus()
    },
    onKeydownConfirmationActive(key) {
      if (key.code === 'Escape' && key.isTrusted)
        this.closeConfirmationPopupActiveStatus()
    },
    onClickOutsideDeleteActive() {
      this.closeDeletePopupActiveStatus()
    },
    onKeydownDeleteActive(key) {
      if (key.code === 'Escape' && key.isTrusted)
        this.closeDeletePopupActiveStatus()
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.practice-general {
  padding: 32px;
  &__tabs {
    flex: initial;
    width: auto;
    padding: 4px;
    border: 1px solid $bh-blue--4;

    &--wrap {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .v-tab {
      color: $bh-blue;
      font-weight: 700;

      &--active {
        background: $bh-blue;
        color: $bh-white !important;
      }
    }
  }

  &--wrap {
    display: flex;
  }

  &-sidebar {
    width: 272px;
    padding: 24px;

    textarea:not([readonly]) {
      padding: 4px;
    }
  }

  &-action-btn {
    cursor: pointer;
    font-weight: 600;
  }

  &-field {
    margin-bottom: $gutter;
  }

  label.bh-text-input__label {
    font-weight: 700;
    line-height: 1.425rem;
    color: $bh-gray--2;
    margin-bottom: 3px;
  }
}
</style>
